'use client'

import { useState, useTransition, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { requestOrgCreation, checkSlugAvailability } from './actions'
import { normalizeSlug } from '@/lib/org/reserved-slugs'

type FieldErrors = Partial<
  Record<'name' | 'slug' | 'email' | 'fullName' | 'attested' | 'general', string>
>

const DRAFT_KEY = 'alumnia_start_form_draft'
const RESEND_COOLDOWN_SECONDS = 30

type Draft = {
  name?: string
  slug?: string
  slugTouched?: boolean
  email?: string
  fullName?: string
  attested?: boolean
}

function readDraft(): Draft {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Draft
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeDraft(d: Draft) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d))
  } catch {
    // Quota or privacy mode — silent fail.
  }
}

function clearDraft() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(DRAFT_KEY)
  } catch {
    // ignore
  }
}

export function StartForm({
  initialEmail = '',
  initialName = '',
  initialSlug = '',
}: {
  initialEmail?: string
  initialName?: string
  initialSlug?: string
}) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [name, setName] = useState(initialName)
  const [slug, setSlug] = useState(initialSlug)
  // Treat a prefilled slug as user-set so typing the name doesn't overwrite it.
  const [slugTouched, setSlugTouched] = useState(!!initialSlug)
  const [email, setEmail] = useState(initialEmail)
  const [fullName, setFullName] = useState('')
  const [attested, setAttested] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitted, setSubmitted] = useState<{ email: string } | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const draftLoadedRef = useRef(false)
  const [slugStatus, setSlugStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid'
  >('idle')

  /* eslint-disable react-hooks/set-state-in-effect --
     One-shot restore from localStorage on mount. setState in effect is the
     correct pattern when hydrating client-only state that the server can't
     render. */
  useEffect(() => {
    const d = readDraft()
    if (d.name) setName(d.name)
    if (d.slug) setSlug(d.slug)
    if (d.slugTouched) setSlugTouched(true)
    if (d.email) setEmail(d.email)
    else if (initialEmail) setEmail(initialEmail)
    if (d.fullName) setFullName(d.fullName)
    if (d.attested) setAttested(true)
    draftLoadedRef.current = true
  }, [initialEmail])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Persist draft to localStorage on every change after the initial restore.
  useEffect(() => {
    if (!draftLoadedRef.current) return
    writeDraft({ name, slug, slugTouched, email, fullName, attested })
  }, [name, slug, slugTouched, email, fullName, attested])

  /* eslint-disable react-hooks/set-state-in-effect --
     Debounced live availability check against an external system (database).
     setState inside the effect is the correct pattern for async debounce. */
  useEffect(() => {
    if (!slug || slug.length < 3) {
      setSlugStatus('idle')
      return
    }
    setSlugStatus('checking')
    let cancelled = false
    const timer = setTimeout(async () => {
      const result = await checkSlugAvailability(slug)
      if (cancelled) return
      if (result.available) setSlugStatus('available')
      else if (result.reason === 'taken') setSlugStatus('taken')
      else if (result.reason === 'reserved') setSlugStatus('reserved')
      else setSlugStatus('invalid')
    }, 350)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [slug])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Resend countdown — ticks every second while > 0.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  function handleNameChange(next: string) {
    setName(next)
    if (!slugTouched) {
      setSlug(normalizeSlug(next).slice(0, 40))
    }
  }

  function buildFormData(): FormData {
    const fd = new FormData()
    fd.set('name', name)
    fd.set('slug', slug)
    fd.set('email', email)
    fd.set('fullName', fullName)
    fd.set('attested', attested ? 'true' : 'false')
    return fd
  }

  function handleSubmit(formData: FormData) {
    setErrors({})
    startTransition(async () => {
      const result = await requestOrgCreation(formData)
      if (!result.success) {
        const field = result.field || 'general'
        setErrors({ [field]: result.error })
        if (field === 'general') toast.error(result.error)
        return
      }
      if ('immediate' in result) {
        clearDraft()
        toast.success('Your network is ready')
        router.push(`/in/${result.orgSlug}/admin/setup?welcome=1`)
        return
      }
      setSubmitted({ email: result.email })
      setResendIn(RESEND_COOLDOWN_SECONDS)
    })
  }

  function handleResend() {
    if (resendIn > 0 || isResending) return
    setIsResending(true)
    void (async () => {
      const result = await requestOrgCreation(buildFormData())
      setIsResending(false)
      if (!result.success) {
        toast.error(result.error)
        return
      }
      if ('checkEmail' in result) {
        toast.success('New link sent — check your inbox')
        setResendIn(RESEND_COOLDOWN_SECONDS)
      }
    })()
  }

  if (submitted) {
    return (
      <div className="text-center space-y-5">
        <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <div className="space-y-2">
          <h2 className="font-serif text-3xl font-bold tracking-tight">Check your email</h2>
          <p className="text-muted-foreground">
            We sent a confirmation link to <strong>{submitted.email}</strong>. Click it to finish
            creating your network.
          </p>
        </div>
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleResend}
            disabled={resendIn > 0 || isResending}
            className="w-full"
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
              </>
            ) : resendIn > 0 ? (
              `Resend in ${resendIn}s`
            ) : (
              'Resend confirmation email'
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            Wrong address?{' '}
            <button
              type="button"
              onClick={() => {
                setSubmitted(null)
                setResendIn(0)
              }}
              className="underline hover:text-foreground"
            >
              Edit and try again
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <form action={handleSubmit} className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="name">Organization name</Label>
        <Input
          id="name"
          name="name"
          placeholder="Horizon Alumni Network"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          maxLength={80}
          autoComplete="organization"
          required
          aria-invalid={!!errors.name}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="slug">Your network URL</Label>
        <div className="flex items-center gap-1 rounded-md border bg-background focus-within:ring-2 focus-within:ring-ring overflow-hidden">
          <span className="pl-3 text-sm text-muted-foreground select-none">alumnia.net/in/</span>
          <input
            id="slug"
            name="slug"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true)
              setSlug(normalizeSlug(e.target.value).slice(0, 40))
            }}
            placeholder="horizon"
            maxLength={40}
            required
            className="flex-1 bg-transparent py-2 pr-3 text-sm outline-none"
            aria-invalid={!!errors.slug}
          />
        </div>
        <div className="min-h-[1rem] text-xs">
          {errors.slug ? (
            <span className="text-destructive">{errors.slug}</span>
          ) : slugStatus === 'checking' ? (
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking…
            </span>
          ) : slugStatus === 'available' ? (
            <span className="text-green-600 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Available
            </span>
          ) : slugStatus === 'taken' ? (
            <span className="text-destructive inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Already taken
            </span>
          ) : slugStatus === 'reserved' ? (
            <span className="text-destructive inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Reserved — pick another
            </span>
          ) : slugStatus === 'invalid' ? (
            <span className="text-muted-foreground">Letters, numbers, and hyphens only</span>
          ) : (
            <span className="text-muted-foreground">3–40 characters</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="fullName">Your name</Label>
          <Input
            id="fullName"
            name="fullName"
            placeholder="Jordan Carter"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            maxLength={80}
            autoComplete="name"
            required
            aria-invalid={!!errors.fullName}
          />
          {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Your work email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@horizon.edu"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-start gap-3 rounded-md border p-3 bg-muted/30">
          <Checkbox
            id="attested"
            name="attested"
            checked={attested}
            onCheckedChange={(c) => setAttested(c === true)}
            className="mt-0.5"
          />
          <label
            htmlFor="attested"
            className="text-sm leading-relaxed text-muted-foreground cursor-pointer"
          >
            I&apos;m authorized to create this organization, and I agree to the{' '}
            <Link
              href="/policies/terms"
              target="_blank"
              className="underline underline-offset-2 hover:text-foreground whitespace-nowrap"
            >
              Terms
            </Link>{' '}
            and{' '}
            <Link
              href="/policies/privacy"
              target="_blank"
              className="underline underline-offset-2 hover:text-foreground whitespace-nowrap"
            >
              Privacy Policy
            </Link>
            .
          </label>
        </div>
        {errors.attested && <p className="text-xs text-destructive">{errors.attested}</p>}
      </div>

      {errors.general && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {errors.general}
        </div>
      )}

      <Button
        type="submit"
        disabled={
          isPending ||
          !attested ||
          slugStatus === 'taken' ||
          slugStatus === 'reserved' ||
          slugStatus === 'checking'
        }
        className="w-full"
        size="lg"
      >
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
          </>
        ) : (
          'Create my network'
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        Free up to 100 members. No credit card required.
      </p>
    </form>
  )
}
